/**
 * Client entry: wires net → prediction/interp → (fx) world renderer, the
 * broadcast HUD (09), audio (08), options (09 §4), results (15 §3.4) and the
 * training lane (09 §5). Nothing here touches `sim/` rules.
 */
import { INPUT_REDUNDANCY, INPUT_SEND_HZ, JUMP_COST, RESULTS_SCREEN_S, SIM_HZ, SPECTATE_EXTRA_DELAY_MS, TICK_DT, TICK_RATE, VIRTUAL_H, VIRTUAL_W } from '../../sim/constants.js'
import { findPlayer } from '../../sim/state.js'
import { DEFAULT_HUB_ARENA_ID, loadArena } from '../../sim/arena.js'
import type { MatchPhase, SimState, Team } from '../../sim/types.js'
import type { FeedbackTag, MatchResultMsg, WelcomeMsg } from '../../shared/protocol.js'
import type { SimEvent } from '../../shared/events.js'
import { createViewport } from './canvas.js'
import { DebugOverlay } from './debug.js'
import { InputSampler, domKeySource } from './input.js'
import { InterpBuffer } from './interp.js'
import { NetClient } from './net.js'
import { Predictor } from './prediction.js'
import { WorldRenderer, fromDiscState, fromInterpDisc, fromInterpPlayer, fromPlayerState, type RenderDisc, type RenderPlayer } from './fx/renderer.js'
import { RenderClock, type ClockFrame } from './fx/hitstop.js'
import { FIXTURES, type Fixture } from './fixture.js'
import { stateToSnapshot } from '../../shared/snapshot.js'
import { FX_TRAIL_SEC } from './fx/constants.js'
import { loadFonts } from './fonts.js'
import { FONT_FILES } from './assets/fonts/files.js'
import { DEFAULT_CAMERA, floorAim, project, type CameraParams } from './camera.js'
import { FixedTeamRig, FollowRig, lensFor, parseCamOverride, rotateMove, type CameraRig } from './camera-rig.js'
import { LOOK_RAD_PER_PX, PAD_LOOK_RAD_PER_S } from './input.js'
import { quantizeAxis } from '../../shared/inputcodec.js'
import { LoadoutMenu, LobbyUI, RunCardUI } from './lobby.js'
import { renderGlyphSheet } from './glyphs.js'
import { SessionUI } from './session.js'
import { drawKiosks } from './kiosk.js'
import { createOptionsStore } from './options.js'
import { AudioSystem, renderAudioDebug, type SoundId } from './audio/index.js'
import { BroadcastHud, setSafeArea, type DiscPipState } from './ui/hud.js'
import { OptionsScreen } from './ui/options-screen.js'
import { ResultsScreen } from './ui/results.js'
import { TRAINING_STEPS, TrainingScript } from './ui/onboarding.js'
import { IdleTimer, PromptEngine, type ActivePrompt } from './ui/prompts.js'
import { makeCallout } from './ui/callouts.js'
import { MenuFlow, MenuScreen, bootMode, liveLine } from './ui/menu.js'
import { assistAngle } from './aim-assist.js'

const params = new URLSearchParams(location.search)
// 14 R9: glyph sheet page.
if (params.get('glyphs') === '1') {
  const c = document.getElementById('game') as HTMLCanvasElement
  c.width = 720; c.height = 1000; c.style.width = '720px'; c.style.height = '1000px'
  renderGlyphSheet(c.getContext('2d')!, c.width, c.height)
  for (const id of ['controls', 'hud']) document.getElementById(id)!.style.display = 'none'
  throw new Error('glyph sheet mode')
}
const debugParam = params.get('debug')
const DEBUG = debugParam === '1' || debugParam === 'audio'
const AUDIO_DEBUG = debugParam === 'audio'
const spectate = params.get('spectate') ?? undefined
const teamParam = params.get('team')
const shaping = { latency: Number(params.get('lag') ?? 0), jitter: Number(params.get('jitter') ?? 0), loss: Number(params.get('loss') ?? 0) / 100 }
// Fidelity V0 `?fixture=<name>[&t=<ms>]`: a canned scene, no socket, frozen render clock (tools/art/frames.mjs).
const fixtureName = params.get('fixture')
/** Fixture epoch: all fixture times are `FIXTURE_EPOCH + atMs` so nothing collides with -1e9 sentinels. */
const FIXTURE_EPOCH = 100_000
let fixture: { fx: Fixture; t: number; frames: number } | null = null

// ── Options (09 §4) ─────────────────────────────────────────────────────────
const options = createOptionsStore()
const name = params.get('name') ?? (options.get('profile.displayName') || undefined)
// `?enter=1` (spec §5): straight into the world — no training lane, no menu.
if (params.get('enter') === '1') options.set('onboarding.completed', true)

const canvas = document.getElementById('game') as HTMLCanvasElement
const vp = createViewport(canvas)
const renderer = new WorldRenderer(vp.ctx)
const clock = new RenderClock()
const hud = new BroadcastHud()
const results = new ResultsScreen()
const input = new InputSampler(domKeySource(canvas, vp.toVirtual))
const debug = new DebugOverlay(document.getElementById('hud')!, DEBUG)
const audioDebugEl = document.getElementById('audiodebug')!
if (AUDIO_DEBUG) audioDebugEl.style.display = ''
const net = new NetClient(undefined, shaping)
const interp = new InterpBuffer()
if (spectate || teamParam) interp.delayMs += SPECTATE_EXTRA_DELAY_MS
const lobbyUI = new LobbyUI(document.getElementById('lobby')!)
const loadout = new LoadoutMenu(document.getElementById('loadout')!)
const runCards = new RunCardUI(document.getElementById('runcards')!)
runCards.onPick = (id) => { net.runCard(id); audio.ui('ui_lock_confirm') }
let hubLoadout: string[] = []
const audio = new AudioSystem({ options: options.values, arena: loadArena(DEFAULT_HUB_ARENA_ID) })
audio.installUnlock([window])
const prompts = new PromptEngine(options)
const session = new SessionUI(document.getElementById('session')!, {
  vsBot: (tier) => net.vsBot(tier),
  rematch: (want) => net.rematch(want),
  leaveToHub: () => { if (net.replaced) net.reclaim(); else net.spectate(null) },
})
lobbyUI.onPick = (ids, removed) => { net.lobbyPick(ids); audio.ui(removed ? 'ui_card_deselect' : 'ui_card_select'); lobbyPicked = true }
lobbyUI.onHover = () => audio.ui('ui_card_hover')
lobbyUI.onLock = (id) => { net.lobbyLock(id); audio.ui('ui_lock_confirm'); lobbyLocked = true }
loadout.onChange = (ids) => { hubLoadout = ids; net.loadout(ids); audio.ui('ui_confirm') }
loadout.onSlot = (index, id) => { net.slot(index, id); audio.ui('ui_card_select') }
const optionsScreen = new OptionsScreen(options, {
  onUiSound: (id) => audio.ui(id as SoundId),
  onNameChange: (n) => net.setName(n),
  onReplayTraining: () => { options.set('onboarding.completed', false); if (!spectator && roomId.startsWith('hub')) net.training(true) },
  onResetIdentity: () => net.resetIdentity(),
  onOpenLoadout: () => { optionsScreen.close(); if (roomId.startsWith('hub') && !spectator) loadout.toggle(hubLoadout, session.disclord) },
  onParty: (code) => net.party(code),
  getParty: () => session.party,
  onLinkIssue: () => net.linkIssue(),
  onLinkRedeem: (code) => net.linkRedeem(code),
  getLinkCode: () => session.linkCode,
})
results.onRowRevealed = () => audio.ui('ui_row_in')
results.onAccolade = () => audio.ui('ui_accolade')

const applyInputOptions = (): void => {
  const o = options.values
  input.gamepad = { aimAssist: o['input.aimAssist'], curve: o['input.stickCurve'], leftDeadzone: o['input.leftDeadzone'], rightDeadzone: o['input.rightDeadzone'] }
  input.setHoldToToggle(o['a11y.holdToToggleShield'])
  input.bindings = options.bindings
}
applyInputOptions()
options.subscribe((o) => { applyInputOptions(); audio.applyOptions(o); vp.setLetterbox(o['video.letterbox']) })
vp.setLetterbox(options.get('video.letterbox'))
vp.onResize(() => setSafeArea(vp.safe()))
setSafeArea(vp.safe())
options.subscribeBindings(() => applyInputOptions())

let predictor: Predictor | null = null
let serverState: SimState | null = null
// Camera rig (spec §2): fixed team camera for matches/spectate/lanes, follow rig in follow arenas.
let rig: CameraRig = new FixedTeamRig()
let cam: CameraParams = DEFAULT_CAMERA
/** Arena the current rig's lens was built for (S10: corridor_xl carries its own `cam`). */
let rigArenaId: string | null = null
/** `?cam=back,height,pitch,fov` — grand-scale camera iteration (2026-09-14); applied over the arena's lens. */
const camOverride = parseCamOverride(params.get('cam'))
if (camOverride) console.info('[cam] override', camOverride)
function pickRig(st: SimState | null): void {
  const want = st?.arena.camera === 'follow' && !spectator ? 'follow' : 'fixed'
  const arenaId = st?.arena.id ?? null
  if (rig.kind === want && arenaId === rigArenaId) return
  const lens = { ...(st ? lensFor(st.arena) : rig.lens), ...(camOverride ?? {}) }
  rig = want === 'follow' ? new FollowRig(lens) : new FixedTeamRig(lens)
  rigArenaId = arenaId
  rig.snap()
  input.lookMode = want === 'follow'
  if (want !== 'follow' && document.pointerLockElement === canvas) document.exitPointerLock()
}
/** Free third-person camera (follow rig): the reticle is fixed on screen and the aim is the floor point under it. */
const RETICLE = { x: VIRTUAL_W / 2, y: VIRTUAL_H * 0.55 }
let cameraTeam: Team = teamParam === 'B' ? 1 : 0
let roomId = '-'
let spectator = false
let playerId = ''
let lastResult: MatchResultMsg | null = null
let resultShownAt = 0
let aimHit: { x: number; z: number } | null = null
let training: TrainingScript | null = null
let trainingExitAt = 0
let trainingDoneHandled = false
let hubEnteredAt = performance.now()
const hubIdle = new IdleTimer()
let lobbyPicked = false, lobbyLocked = false
let pressedQueue = false, pressedSpectate = false
let activePrompt: ActivePrompt | null = null
let lastPhase: MatchPhase | null = null

// ── UI ──────────────────────────────────────────────────────────────────────
const queueBtn = document.getElementById('queue') as HTMLButtonElement
const roomSel = document.getElementById('rooms') as HTMLSelectElement
let wantMatch = false
const toggleQueue = () => { if (training) return; wantMatch = !wantMatch; queueBtn.classList.toggle('on', wantMatch); net.setQueue(wantMatch); audio.ui(wantMatch ? 'ui_queue_on' : 'ui_queue_off'); pressedQueue = true }
queueBtn.onclick = toggleQueue
document.getElementById('debug')!.onclick = (e) => { debug.toggle(); (e.target as HTMLElement).classList.toggle('on', debug.visible) }
;(document.getElementById('debug') as HTMLElement).classList.toggle('on', DEBUG)
roomSel.onchange = () => { const v = roomSel.value; net.spectate(v === 'hub' ? null : v) }
input.events = {
  queue: toggleQueue,
  spectate: () => { pressedSpectate = true; const opts = Array.from(roomSel.options, (o) => o.value); if (!opts.length) return; const i = (opts.indexOf(roomSel.value) + 1) % opts.length; roomSel.value = opts[i]; roomSel.onchange!(new Event('change')) },
  camera: () => { if (spectator || DEBUG) { cameraTeam = cameraTeam === 0 ? 1 : 0; audio.setTeamSwitch(cameraTeam) } },
  feedback: () => {
    if (flow.state !== 'in') return
    feedbackOpen = !feedbackOpen
    fbEl.textContent = feedbackOpen ? 'MARK: [1] UNFAIR  [2] SLOW  [3] FAST  [4] CONFUSING  [ESC] CANCEL' : ''
    fbEl.style.display = feedbackOpen ? '' : 'none'
  },
  menu: () => {
    if (training && !training.done) { if (training.skipPending) training.cancelSkip(); else training.requestSkip(); return }
    if (results.visible && spectator) { results.hide(); net.spectate(null); return }
    optionsScreen.toggle()
  },
}
// 13 §2 `/feedback` marker prompt: its own overlay so it never fights SessionUI.render.
const fbEl = document.createElement('div')
fbEl.id = 'feedback'
fbEl.style.cssText = 'position:fixed;left:50%;top:22%;transform:translateX(-50%);color:#E0A33B;font-size:15px;letter-spacing:1.5px;text-shadow:0 1px 0 #000;display:none;pointer-events:none'
document.body.appendChild(fbEl)
let feedbackOpen = false
window.addEventListener('keydown', (e) => {
  hubIdle.activity(performance.now())
  if (feedbackOpen) {
    const tag = ({ Digit1: 'unfair', Digit2: 'slow', Digit3: 'fast', Digit4: 'confusing' } as Record<string, FeedbackTag | undefined>)[e.code]
    if (tag) { net.feedback(tag); session.say(`MARKED: ${tag.toUpperCase()}`, performance.now(), 2000); audio.ui('ui_confirm') }
    if (tag || e.code === 'Escape') { feedbackOpen = false; fbEl.style.display = 'none'; e.preventDefault(); return }
  }
  if (e.code === 'Enter' && net.replaced) { session.status = 'connected'; net.reclaim(); return }
  // 12 §4: [R] in the hub starts a run (W1). Party members in the same hub come along (co-op).
  if (e.code === 'KeyR' && roomId.startsWith('hub') && !spectator && !optionsScreen.visible && !training && flow.state === 'in') { net.runStart(1); audio.ui('ui_confirm'); return }
  // D11: [B] accepts the vs-BOT offer once Z12 shows it.
  if (e.code === 'KeyB' && session.queue.vsBotAvailable && roomId.startsWith('hub') && !spectator && !optionsScreen.visible) { net.vsBot(); audio.ui('ui_confirm'); return }
  if (e.code === 'Enter' && training?.skipPending) { training.confirmSkip(performance.now()); return }
  if (results.visible && !spectator) { const b = results.key(e.code); if (b) activateResultsButton(b) }
})
canvas.addEventListener('click', (e) => {
  // Free camera: a click in a follow-camera room grabs the pointer for mouse-look (Escape releases it).
  if (rig.kind === 'follow' && !results.visible && !optionsScreen.visible && !spectator && document.pointerLockElement !== canvas) { canvas.requestPointerLock?.(); return }
  if (!results.visible || spectator) return
  const p = vp.toVirtual(e.clientX, e.clientY)
  const b = results.hitTest(p.x, p.y)
  if (b) activateResultsButton(b)
})
function activateResultsButton(b: 'hub' | 'rematch' | 'spectate'): void {
  audio.ui('ui_confirm')
  if (b === 'hub') { results.hide(); net.spectate(null) }
  else if (b === 'spectate') { const id = liveMatchElsewhere(); if (id) { results.hide(); net.spectate(id) } }
  else { session.rematchWanted = !session.rematchWanted; net.rematch(session.rematchWanted) }
}
/** 10 §3: another live match to spectate from the results screen, if any. */
function liveMatchElsewhere(): string | null { return session.kiosk?.live.find((l) => l.id !== roomId && !l.full)?.id ?? null }
window.addEventListener('blur', () => audio.onBlur())
window.addEventListener('focus', () => audio.onFocus())

// ── Net wiring ──────────────────────────────────────────────────────────────
function enter(w: { playerId: string; team: Team | null; spectator: boolean; roomId: string; state: SimState }): void {
  roomId = w.roomId
  spectator = w.spectator
  cameraTeam = w.team ?? (teamParam === 'B' ? 1 : 0)
  input.team = cameraTeam
  serverState = w.state
  // Seq continues across the rebuild (10 §6 reconnect, room transfer): see PredictorOptions.seqStart.
  predictor = w.spectator ? null : new Predictor(w.playerId, w.state, { seqStart: predictor?.seq })
  wantMatch = false; queueBtn.classList.remove('on')
  lastResult = null
  results.hide()
  session.setResult(null)
  session.setPause(null, performance.now())
  audio.setListener(cameraTeam)
  audio.hardResync()
  hubEnteredAt = performance.now()
  hubIdle.reset(hubEnteredAt)
  hud.callouts.clear()
  pickRig(serverState)
  onRoomChanged()
}
function onRoomChanged(): void {
  const now = performance.now()
  rig.snap()
  if (roomId.startsWith('lane-')) { training = new TrainingScript(input.usingGamepad ? 'pad' : 'mouse', now); trainingExitAt = 0; trainingDoneHandled = false }
  else {
    training = null
    if (roomId.startsWith('hub')) { audio.ui('hub_enter'); if (!spectator && !options.get('onboarding.completed')) net.training(true) }
  }
}
net.on('welcome', (w: WelcomeMsg) => { playerId = w.playerId; enter(w) })
net.on('joined', (j) => { if (j.roomId.startsWith('hub')) { session.run = null; runCards.hide() } const wasMatch = roomId.startsWith('m'); if (wasMatch && j.roomId.startsWith('hub')) session.say('BACK IN THE HUB — [TAB] TO QUEUE AGAIN', performance.now(), 4000); roomId = j.roomId; spectator = j.spectator; cameraTeam = j.team ?? cameraTeam; input.team = cameraTeam; if (spectator) predictor = null; audio.setListener(cameraTeam); net.requestResync(); results.hide(); onRoomChanged() })
net.on('resync', (r) => {
  serverState = r.state
  session.knobs = r.state.knobs && Object.keys(r.state.knobs).length ? { ...r.state.knobs } : null
  pickRig(r.state)
  if (!spectator) { if (predictor) predictor.resync(r.state); else predictor = new Predictor(playerId, r.state) }
  audio.hardResync()
})
net.on('snapshot', (s) => {
  interp.push(s)
  const now = performance.now()
  if (predictor) predictor.reconcile(s, now, net.serverTick)
  if (!predictor && serverState) { serverState.tick = s.tick; serverState.match.phase = s.match.phase; serverState.match.score = s.match.score; serverState.match.timerTicks = s.match.timerTicks; serverState.match.overtimeTicks = s.match.overtimeTicks; serverState.match.bounds = s.match.bounds; serverState.match.paused = s.match.paused; serverState.match.roundResults = s.match.roundResults; serverState.match.roundIndex = s.match.roundIndex }
  lobbyUI.update(s.match.phase, s.match.lobby, playerId, spectator ? null : cameraTeam, s.players.map((p) => ({ id: p.id, name: p.name, team: p.team })), spectator)
  ingestEvents(s.events, now, s)
})
net.on('matchResult', (m) => {
  lastResult = m; session.setResult(m); resultShownAt = performance.now()
  const st = predictor?.state ?? serverState
  results.show(m, { localId: spectator ? null : playerId, localTeam: spectator ? null : cameraTeam, spectator, now: resultShownAt, participants: m.players.filter((p) => !p.bot).length })
  hud.callouts.clear('matchResult')
  void st
})
net.on('queueState', (q) => {
  // 08 §4 `ui_queue_count`: another player flagged/unflagged (own toggles play ui_queue_on/off).
  if (q.flagged !== session.queue.flagged && (q.state === 'FLAGGED') === wantMatch) audio.ui('ui_queue_count')
  session.setQueue(q); wantMatch = q.state === 'FLAGGED'; queueBtn.classList.toggle('on', wantMatch); if (q.state === 'PULLED') audio.ui('match_pull')
})
let kioskHeadline: string | null = null
net.on('kiosk', (k) => {
  // 08 §4 `kiosk_tick`: headline change, spatialised at the listener-side kiosk board (10 §4 positions).
  const headline = k.live.length ? k.live.map((m) => `${m.teams[0].join('+')}v${m.teams[1].join('+')}:${m.score[0]}-${m.score[1]}`).join('|') : k.lastResult ? `last:${JSON.stringify(k.lastResult)}` : k.drain ? 'drain' : 'idle'
  if (kioskHeadline !== null && headline !== kioskHeadline && roomId.startsWith('hub')) { const st = predictor?.state ?? serverState; if (st) audio.at('kiosk_tick', { x: 0, z: cameraTeam === 0 ? 0.6 : st.arena.length - 0.6 }) }
  kioskHeadline = headline
  session.kiosk = k
})
net.on('pause', (p) => { session.setPause(p, performance.now()); audio.pause(true) })
net.on('resume', (r) => { session.setResume(r.inTicks, performance.now()); audio.pause(false) })
net.on('spectateFull', () => session.say('SPECTATORS FULL', performance.now()))
net.on('idleWarning', (r) => session.say(`IDLE — PRESS ANY KEY (${Math.ceil(r.kickInMs / 1000)}s)`, performance.now(), r.kickInMs))
net.on('vsBotDenied', () => { session.say('vs BOT not available', performance.now()); audio.ui('ui_error') })
net.on('trainingDenied', () => { options.set('onboarding.completed', true); session.say('TRAINING UNAVAILABLE', performance.now()) })
net.on('serverRestart', () => session.say('SERVER RESTARTING — RECONNECTING…', performance.now(), 10_000))
net.on('nameResult', (r) => session.say(r.ok ? `NAME SET: ${r.shown}` : `NAME NOT ALLOWED — using ${r.shown}`, performance.now()))
net.on('sessionReplaced', () => { session.status = 'replaced' })
net.on('disclord', (d) => {
  const prev = session.disclord
  session.disclord = d
  if (prev && d.level > prev.level) { session.say(`LEVEL ${d.level}`, performance.now(), 4000); audio.ui('ui_accolade') }
  else if (prev && d.xp > prev.xp) session.say(`+${d.xp - prev.xp} XP`, performance.now(), 3000)
})
net.on('runState', (r) => { session.run = r; runCards.update(r); if (r.stage === 'RESULTS') audio.ui(r.outcome === 'won' ? 'ui_accolade' : 'ui_error') })
net.on('runDenied', () => { session.say('RUN UNAVAILABLE — from the hub, not while flagged or spectating', performance.now()); audio.ui('ui_error') })
net.on('linkCode', (c) => { session.linkCode = c; if (optionsScreen.visible) optionsScreen.render() })
net.on('linkResult', (r) => {
  session.say(r.ok ? 'DEVICE LINKED — your disclord follows you here' : r.reason === 'EXPIRED' ? 'LINK CODE EXPIRED' : r.reason === 'SELF' ? 'THAT IS THIS ACCOUNT' : 'BAD LINK CODE', performance.now(), 4000)
  audio.ui(r.ok ? 'ui_accolade' : 'ui_error')
})
net.on('slotResult', (r) => {
  if (r.ok) { session.say(`SLOT ${r.index} SET — applies on your next respawn${r.tokensSpent ? ' · −1 IMPRINT TOKEN' : ''}`, performance.now(), 3000); audio.ui('ui_confirm') }
  else { session.say(r.reason === 'INCOMPATIBLE' ? `INCOMPATIBLE — ${(r.keys ?? []).join(', ')}` : r.reason === 'LOCKED_SLOT' ? 'SLOT LOCKED — level up' : r.reason === 'NOT_UNLOCKED' ? 'NOT IN YOUR KIT' : r.reason === 'NO_TOKENS' ? 'RE-SLOTTING COSTS AN IMPRINT TOKEN — clear a run to earn one' : 'BAD SLOT', performance.now(), 3500); audio.ui('ui_error') }
})
net.on('partyState', (p) => {
  session.party = p
  if (p.error) { session.say(p.error === 'FULL' ? 'PARTY FULL' : 'BAD PARTY CODE', performance.now()); audio.ui('ui_error') }
  else session.say(p.code ? `PARTY ${p.code} · ${p.members.length}/2` : 'LEFT PARTY', performance.now(), 3000)
  if (optionsScreen.visible) optionsScreen.render()
})
net.on('reconnected', (w) => { session.status = 'connected'; session.setPause(null, performance.now()); playerId = w.playerId; enter(w) })
net.on('rooms', (r) => {
  const cur = roomSel.value
  roomSel.innerHTML = ''
  for (const room of r.rooms) { const o = document.createElement('option'); o.value = room.id; o.textContent = `${room.id} ${room.phase} ${room.score[0]}-${room.score[1]} (${room.players}p/${room.spectators}s)`; roomSel.appendChild(o) }
  roomSel.value = r.rooms.some((x) => x.id === cur) ? cur : roomId
})
net.on('kicked', (why) => console.warn('kicked:', why))
net.on('disconnect', () => { if (!net.replaced) session.status = 'reconnecting' })
// ── Boot: loading → menu → hello (spec §5). Machine entry points skip straight to the hello. ──
const controlsEl = document.getElementById('controls')!
const flow = new MenuFlow(bootMode(params) === 'skip')
const menuScreen = new MenuScreen(document.getElementById('loading')!, document.getElementById('menu')!, {
  onEnter: (n) => enterWorld(n),
  onOptions: () => optionsScreen.toggle(),
}, options.get('profile.displayName'))
let menuLive: string | null = null
let enterStarted = false
function enterWorld(typed?: string): void {
  if (enterStarted || !(flow.state === 'menu' || flow.state === 'entering')) return
  enterStarted = true
  flow.enter()
  const chosen = params.get('name') ?? (typed && typed.length >= 3 ? typed : undefined) ?? (options.get('profile.displayName') || undefined)
  if (typed && typed.length >= 3 && typed !== options.get('profile.displayName')) options.set('profile.displayName', typed)
  void audio.unlock() // ENTER is the unlock gesture
  net.connect({ name: chosen, spectate })
    .then(() => { flow.entered(); controlsEl.classList.add('in') })
    .catch((e) => { console.error('connect failed', e); enterStarted = false; flow.state = 'menu'; session.say('CONNECT FAILED — RETRY', performance.now()) })
}
async function boot(): Promise<void> {
  // 15 §3.3: the webfonts gate the first HUD frame for at most 1.5 s, then the fallback stacks carry on.
  const fonts = await loadFonts(FONT_FILES)
  if (fonts.timedOut) console.warn('[fonts] timed out, fallback stack in use')
  if (fixtureName) { renderer.prewarmBake(loadArena(), vp.scale * vp.dpr); applyFixture(fixtureName); return }
  const link = net.open().then(() => flow.progress('LINK', 1), (e) => { console.error('link failed', e); flow.progress('LINK', 1) })
  await audio.prewarm((p) => flow.progress('SOUND BANK', p))
  renderer.prewarmBake(loadArena(DEFAULT_HUB_ARENA_ID), vp.scale * vp.dpr); flow.progress('ARENA BAKE', 1) // D15: the hub players land on
  await link
  if (flow.state === 'entering') enterWorld() // skip mode: MenuFlow moved straight to entering
}
void boot()
/** Fixture mode: install the canned scene as if it were a welcome + one snapshot, then freeze. */
function applyFixture(name: string): void {
  const make = FIXTURES[name]
  if (!make) { console.error(`[fixture] unknown "${name}"; known: ${Object.keys(FIXTURES).join(', ')}`); return }
  const fx = make()
  const t = Number(params.get('t') ?? fx.t)
  fixture = { fx, t, frames: 0 }
  roomId = fx.mode === 'hub' ? 'hub-fixture' : 'm-fixture'
  spectator = fx.spectator; cameraTeam = fx.cameraTeam; input.team = cameraTeam
  serverState = fx.state
  playerId = fx.localId ?? ''
  predictor = fx.localId && !fx.spectator ? new Predictor(fx.localId, fx.state) : null
  // Two identical snapshots 1 s apart: interp samples at 500 ms and gets constant positions.
  interp.push(stateToSnapshot(fx.state, 0))
  interp.push({ ...stateToSnapshot(fx.state, 1000), tick: fx.state.tick + 30 })
  // Trails need history the frozen clock cannot accumulate: seed FX_TRAIL_SEC of travel behind each moving disc.
  for (const d of fx.state.discs) {
    if (d.state !== 'FLIGHT' && d.state !== 'RETURNING') continue
    for (let k = 12; k >= 1; k--) { const back = (FX_TRAIL_SEC * k) / 12; renderer.trails.push(d.id, d.state, d.team, d.pos.x - d.vel.x * back, d.pos.z - d.vel.z * back, 500 - back * 1000) }
  }
  flow.state = 'in'; controlsEl.classList.add('in')
  pickRig(serverState); rig.snap()
  const now = FIXTURE_EPOCH
  const s = { players: fx.state.players.map((p) => ({ id: p.id, name: p.name, team: p.team, x: p.pos.x, z: p.pos.z, held: p.discsHeld })), discs: fx.state.discs.map((d) => ({ id: d.id, x: d.pos.x, z: d.pos.z })), match: { phase: fx.state.match.phase } }
  for (const e of fx.events) if (e.atMs <= t) ingestEvents([e.ev], now + e.atMs, s)
  if (fx.result) { lastResult = fx.result; resultShownAt = now + t - 6000; results.show(fx.result, { localId: fx.localId, localTeam: fx.spectator ? null : fx.cameraTeam, spectator: fx.spectator, now: resultShownAt, participants: fx.result.players.filter((p) => !p.bot).length }) }
}
// Menu live line: /api/rooms every 5 s while the menu is showing.
async function pollLive(): Promise<void> {
  if (fixtureName) return
  if (flow.state !== 'in') {
    try { const r = await fetch('/api/rooms'); if (r.ok) menuLive = liveLine(((await r.json()) as { rooms: { kind: string; players: number }[] }).rooms) } catch { /* offline: keep the last line */ }
  }
  setTimeout(() => void pollLive(), 5000)
}
void pollLive()

// ── Snapshot events → feel / HUD / audio / training / prompts ───────────────
function ingestEvents(events: SimEvent[], now: number, s: { players: { id: string; name: string; team: Team; x: number; z: number; held: number[] }[]; discs: { id: number; x: number; z: number }[]; match: { phase: MatchPhase } }): void {
  const st = predictor?.state ?? serverState
  const hub = roomId.startsWith('hub') || roomId.startsWith('lane-')
  const o = options.values
  const names = (id: string) => st?.players.find((p) => p.id === id)?.name ?? s.players.find((p) => p.id === id)?.name ?? id
  const teamOf = (id: string): Team => (st?.players.find((p) => p.id === id)?.team ?? s.players.find((p) => p.id === id)?.team ?? 0) as Team
  const posOf = (id: string) => { const p = s.players.find((q) => q.id === id); return p ? { x: p.x, z: p.z } : undefined }
  const discPos = (id: number) => { const d = s.discs.find((q) => q.id === id); return d ? { x: d.x, z: d.z } : undefined }
  // 11 §2: hitstop from confirmed eliminations only.
  let countA = 0, countB = 0
  for (const e of events) {
    if (e.type === 'derez') { if (teamOf(e.playerId) === 0) countA++; else countB++ }
    if (e.type === 'rescind') clock.rescind(now)
  }
  if (countA + countB > 0) clock.onEliminations(now, { countA, countB, hub, reducedMotion: o['a11y.reducedMotion'], flashReduction: o['a11y.flashReduction'], hubHitstop: o['a11y.hubHitstop'] })
  renderer.ingest(events, now, { localId: spectator ? null : playerId, hub, positions: posOf, discPositions: discPos }, o['a11y.reducedMotion'])
  const kick = renderer.takeClashKick()
  if (kick !== null) clock.kick(kick, 0.33, 100, o['a11y.reducedMotion'])
  hud.ingest(events, now, { localTeam: spectator ? null : cameraTeam, spectator, hub, localId: playerId, names, teamOf })
  audio.ingest(events, {
    localId: spectator ? null : playerId, localTeam: spectator ? null : cameraTeam, hub, phase: s.match.phase, posOf, discPos, teamOf: (id) => teamOf(id),
    heldDiscs: () => s.players.map((p) => ({ playerId: p.id, count: p.held.length })),
    playerIds: () => s.players.map((p) => p.id),
    listenerBackEdge: () => (cameraTeam === 0 ? { x: 0, z: 0 } : { x: 0, z: st?.arena.length ?? 30 }),
    hitInBatch: events.some((e) => e.type === 'hit'),
  })
  // Training lane (09 §5).
  if (training && st) {
    const me = findPlayer(st, playerId)
    const bot = st.players.find((p) => p.isBot)
    if (training.observe({ now, mePos: me ? { x: me.pos.x, z: me.pos.z } : null, meAlive: !!me?.alive, events, localId: playerId, botId: bot?.id ?? null })) audio.ui('ui_tutorial_step')
    // Completion may land between frames (Escape → Enter skip), so latch on a flag rather than a per-frame edge.
    if (training.done && !trainingDoneHandled) { trainingDoneHandled = true; options.set('onboarding.completed', true); hud.callouts.push(makeCallout('trainingDone', 'TRAINING COMPLETE', now)); trainingExitAt = now + 2000 }
  }
  // Contextual prompts (09 §5.2).
  const me = st ? findPlayer(st, playerId) : undefined
  const ph = st?.mode === 'match' ? s.match.phase : null
  activePrompt = prompts.evaluate({
    now, hub, phase: ph, queueOff: !wantMatch, othersFlagged: Math.max(0, session.queue.flagged - (wantMatch ? 1 : 0)), hubIdleS: hubIdle.idleS(now), hubTimeS: (now - hubEnteredAt) / 1000,
    enemyGroundDiscNear: !!(me && st && st.discs.some((d) => d.state === 'GROUND' && d.team !== me.team && Math.hypot(d.pos.x - me.pos.x, d.pos.z - me.pos.z) < 3)),
    events, meState: me?.state ?? null, heldCount: me?.discsHeld.length ?? 0, liveMatchExists: Array.from(roomSel.options).some((op) => op.value.startsWith('match')), localId: playerId,
    pressed: { queue: pressedQueue, spectate: pressedSpectate }, lobbyPick: lobbyPicked, lobbyLocked,
  })
  pressedQueue = pressedSpectate = false
}

// ── Fixed prediction tick (rAF accumulator + setTimeout fallback for hidden tabs) ──
let acc = 0
let last = performance.now()
function predictionTick(now: number): void {
  if (!predictor || spectator) return
  const me = predictor.local
  if (me) {
    const cur = rig.kind === 'follow' ? RETICLE : (input.cursor ?? input.lastCursor)
    const fa = floorAim(cur, predictor.renderPos() ?? me.pos, cameraTeam, predictor.state.arena, input.aim, cam)
    aimHit = fa.hit
    // 05 §3: assist is gamepad-only by spec; `input.mouseAimAssist` is the 2026-09-13 playtest
    // experiment (default off). The reticle stays where the cursor is — only the bearing snaps.
    const mouseAngle = options.get('input.mouseAimAssist') && !input.usingGamepad && me.alive ? (assist(fa.angle) ?? fa.angle) : fa.angle
    if (rig.kind === 'follow') input.aim = mouseAngle; else input.setMouseAim(mouseAngle)
  }
  const padToWorld = (v: { x: number; z: number }) => Math.atan2(cameraTeam === 0 ? v.z : -v.z, cameraTeam === 0 ? v.x : -v.x)
  function assist(a: number): number | null {
    if (!predictor || !me) return null
    return assistAngle(a, { x: me.pos.x, z: me.pos.z }, predictor.state.players.filter((q) => q.team !== me.team && q.alive).map((q) => ({ x: q.pos.x, z: q.pos.z })))
  }
  const sampled = input.sample(now, padToWorld, assist)
  // Spec §2: screen-space move → team-local for a yawed camera, before the sim sees it.
  if (rig.kind === 'follow') { const r = rotateMove(sampled.mx, sampled.my, cam.pose.yaw); sampled.mx = r.mx; sampled.my = r.my }
  // Predict with the wire-quantised axes so the local step and the server's agree bit-for-bit.
  sampled.mx = quantizeAxis(sampled.mx); sampled.my = quantizeAxis(sampled.my)
  if (optionsScreen.visible) { sampled.throw = false; sampled.dash = false; sampled.jump = false; sampled.shield = false; sampled.mx = 0; sampled.my = 0 }
  if (sampled.mx !== 0 || sampled.my !== 0 || sampled.throw || sampled.shield || sampled.dash || sampled.jump) hubIdle.activity(now)
  if (sampled.dash && me && me.alive && (me.state === 'IDLE' || me.state === 'THROW_RECOVERY') && me.dashCooldownTicks === 0 && me.stamina < me.tuning.DASH_COST) audio.staminaRefuse()
  // S10: a jump refused only for stamina gets the same dry double-tick (spec §1.3 canJump; 08 §4b stamina_refuse).
  if (sampled.jump && me && me.alive && me.grounded && me.landTicks === 0 && (me.state === 'IDLE' || me.state === 'THROW_RECOVERY' || me.state === 'EXHAUST') && me.stamina < JUMP_COST) audio.staminaRefuse()
  predictor.apply(sampled)
  if (me && me.alive) net.sendInputs(predictor.pending.tail(INPUT_REDUNDANCY))
}
function runTicks(now: number): void {
  const dt = Math.min(0.25, (now - last) / 1000)
  last = now
  acc += dt
  let n = 0
  while (acc >= TICK_DT && n < 8) { acc -= TICK_DT; n++; predictionTick(now) }
  predictor?.smooth(dt)
}
setInterval(() => { if (document.hidden) runTicks(performance.now()) }, 1000 / INPUT_SEND_HZ)

// ── Render ──────────────────────────────────────────────────────────────────
let fps = 0, fpsFrames = 0, fpsAt = last, lastDrawAt = 0, lastCamAt = last
// Perf instrumentation: main-thread busy time per frame (ring of 1200), read by tools/perf-client.mjs.
const frameMs = new Float32Array(1200); let frameMsN = 0
function frameStats(): { n: number; p50: number; p95: number; p99: number; max: number } {
  const arr = Array.from(frameMs.subarray(0, Math.min(frameMsN, frameMs.length))).sort((a, b) => a - b)
  const q = (p: number) => arr[Math.min(arr.length - 1, Math.floor((p / 100) * arr.length))] ?? 0
  return { n: arr.length, p50: q(50), p95: q(95), p99: q(99), max: arr[arr.length - 1] ?? 0 }
}
function resetFrameStats(): void { frameMsN = 0 }
function discPips(st: SimState, meId: string): DiscPipState[] {
  const me = findPlayer(st, meId)
  if (!me) return []
  const out: DiscPipState[] = []
  for (const id of me.discsHeld) { void id; out.push('held') }
  for (const d of st.discs) if (d.homeOwner === meId && d.holder !== meId && out.length < me.maxDiscs) out.push(d.state === 'FLIGHT' ? 'flight' : d.state === 'RETURNING' ? 'returning' : 'ground')
  while (out.length < me.maxDiscs) out.push('empty')
  return out
}
let frameErrors = 0
function frame(now: number): void {
  const t0 = performance.now()
  try { frameBody(now) } catch (err) {
    // A throw inside one frame must never stop the loop; log (rate-limited) and carry on.
    if (frameErrors++ < 10) console.error('[frame]', err)
  } finally {
    frameMs[frameMsN++ % frameMs.length] = performance.now() - t0
    requestAnimationFrame(frame)
  }
}
function frameBody(realNow: number): void {
  const now = fixture ? FIXTURE_EPOCH + fixture.t : realNow
  menuScreen.render(flow, menuLive, now)
  if (!document.hidden && !fixture) runTicks(now)
  const cap = options.get('video.fpsCap')
  if (cap !== 'vsync' && now - lastDrawAt < 1000 / Number(cap) - 1) return
  lastDrawAt = now
  vp.begin()
  const st = predictor?.state ?? serverState
  const me = predictor ? findPlayer(predictor.state, predictor.playerId) : undefined
  const o = options.values
  const hub = roomId.startsWith('hub') || roomId.startsWith('lane-')
  const cf: ClockFrame = fixture ? { renderTime: 500, frozen: false, flash: null, strokeOnlyFrame: false, shake: { x: 0, y: 0 }, remainingMs: 0, releaseAt: -Infinity } : clock.update(now, net.serverNow())
  if (st) {
    const players: RenderPlayer[] = []
    const discs: RenderDisc[] = []
    const outlineHeld = st.mode === 'match' && st.match.phase === 'COUNTDOWN'
    if (me && predictor) {
      players.push(fromPlayerState(me, predictor.renderPos()!, st.tick, true))
      for (const d of predictor.state.discs) if (d.holder === me.id || (d.state === 'FLIGHT' && d.thrower === me.id)) discs.push(fromDiscState(d, outlineHeld && d.state === 'HELD'))
    }
    for (const r of interp.sample(cf.renderTime, me?.id)) players.push(fromInterpPlayer(r, st.tick))
    for (const d of interp.sampleDiscs(cf.renderTime, me?.id)) discs.push(fromInterpDisc(d, outlineHeld && d.st === 'HELD'))
    const rp = me && predictor ? predictor.renderPos() : null
    const camDt = Math.min(0.1, (now - lastCamAt) / 1000)
    const lookYaw = optionsScreen.visible ? (input.takeLook(), 0) : input.takeLook() * LOOK_RAD_PER_PX + input.padLookX * PAD_LOOK_RAD_PER_S * camDt
    cam = rig.update({ dt: camDt, localPos: rp ? { x: rp.x, z: rp.z } : null, aimAngle: input.aim, lookYaw, team: cameraTeam, arenaLength: st.arena.length })
    lastCamAt = now
    audio.setCamera(cam, st.arena)
    renderer.draw({
      state: st, cameraTeam, spectator, localId: me ? me.id : null, players, discs, aimHit: me && me.alive ? aimHit : null, aimAngle: input.aim, localPos: me ? { x: me.pos.x, z: me.pos.z } : null,
      showPreview: o['hud.bouncePreview'] && !!me && me.alive, lead: o['hud.leadMarker'] && me && me.alive ? { speed: me.tuning.DISC_THROW_SPEED, windupS: me.tuning.THROW_WINDUP_TICKS / SIM_HZ } : null, debug: debug.visible, now, renderTime: cf.renderTime, clock: cf,
      options: { reducedMotion: o['a11y.reducedMotion'], flashReduction: o['a11y.flashReduction'], highContrast: o['a11y.highContrast'], patternStrength: o['a11y.patternStrength'], offscreenDiscs: o['hud.offscreenDiscs'] && !spectator },
      mode: st.mode, cam, resultsSinceMs: results.visible ? now - resultShownAt : null,
    }, vp.scale * vp.dpr)
    if (st.mode === 'hub') drawKiosks(vp.ctx, st.arena, cameraTeam, session.kiosk, now, cam)
    // Audio director + music (08 §5/§6).
    const timerS = st.mode === 'match' && st.match.phase === 'LIVE' ? st.match.timerTicks / SIM_HZ : null
    if (!fixture) audio.frame({
      discs: discs.map((d) => ({ id: d.id, st: d.st, x: d.x, z: d.z, speed: Math.hypot(d.vx, d.vz), team: d.team })),
      players: players.map((p) => ({ id: p.id, st: p.st, x: p.x, z: p.z, alive: p.alive, shieldAgeTicks: p.sag, team: p.team, g: p.g })),
      phase: st.match.phase, mode: st.mode, timerS, cameraTeam, phaseS: st.match.phaseTicks / SIM_HZ, bounds: st.match.phase === 'OVERTIME' ? st.match.bounds : undefined,
    })
    const musicState = results.visible ? 'RESULTS' : AudioSystem.musicFor(st.mode, st.match.phase, timerS)
    if (!fixture) audio.setMusic(musicState, { roundIndex: st.match.roundIndex, now })
    if (st.match.phase !== lastPhase) { lastPhase = st.match.phase; if (st.mode === 'match' && (st.match.phase === 'LOBBY_PICK')) { lobbyPicked = lobbyLocked = false } }
    // HUD (09).
    const teammate = me ? st.players.find((p) => p.team === me.team && p.id !== me.id && !p.isBot) ?? st.players.find((p) => p.team === me.team && p.id !== me.id) ?? null : null
    const pauseLeft = session.pause ? Math.max(0, session.pause.graceEndsInMs - (now - session.pause.at)) : 0
    const resumeN = session.resumeAt !== null && now < session.resumeAt ? Math.max(1, Math.min(3, Math.ceil(((session.resumeAt - now) / 1000) * 1.5))) : null
    hud.draw(vp.ctx, {
      state: st, me: me ?? null, localTeam: spectator ? null : cameraTeam, spectator, eliminated: !!me && !me.alive && st.mode === 'match', mode: st.mode,
      ping: { rtt: net.rtt, resyncFlashUntil: net.lastResyncAt + 1500 }, queue: hub && !spectator && !training ? session.queue : null,
      names: (id) => st.players.find((p) => p.id === id)?.name ?? id, teammate, discStates: me ? discPips(st, me.id) : [], powerUp: me?.powerUps[0] ?? null, hubLoadout, disclord: hub ? session.disclord : null,
      pause: session.pause ? { name: session.pause.name, leftMs: pauseLeft } : null, resume: resumeN, reconnecting: session.status === 'reconnecting', fps: o['video.showFps'] ? fps : undefined,
      device: input.usingGamepad ? 'pad' : 'mouse', options: o,
      trainingStep: training ? { n: training.step, text: training.done ? 'TRAINING COMPLETE' : training.instruction(), hint: training.hint(now), total: TRAINING_STEPS, skipPending: training.skipPending } : undefined,
      prompt: activePrompt ? { text: activePrompt.text } : null,
    }, now)
    // Results (15 §3.4).
    if (results.visible && lastResult && (st.match.phase === 'RESULTS' || st.match.phase === 'DONE' || st.mode === 'match')) {
      results.setLiveMatch(liveMatchElsewhere())
      results.draw(vp.ctx, now, { reducedMotion: o['a11y.reducedMotion'], rematchVotes: lastResult.rematch.length, autoReturnLeftMs: Math.max(0, RESULTS_SCREEN_S * 1000 - (now - resultShownAt)), rematchPressed: session.rematchWanted })
    } else if (results.visible && st.mode === 'hub') results.hide()
    // Training lane bot script + exit.
    if (training) {
      const cmd = training.botCommand(now)
      if (cmd) net.trainingBot(cmd)
      if (training.done && trainingExitAt && now >= trainingExitAt) { trainingExitAt = 0; net.training(false) }
    }
  }
  session.render(now, hub && !spectator, st?.match.phase ?? '-')
  if (fixture && ++fixture.frames === 2) (window as unknown as { __fixtureReady: boolean }).__fixtureReady = true
  fpsFrames++
  if (now - fpsAt > 500) { fps = (fpsFrames * 1000) / (now - fpsAt); fpsFrames = 0; fpsAt = now }
  if (AUDIO_DEBUG && fpsFrames % 4 === 0) renderAudioDebug(audioDebugEl, audio.debug())
  const fx = renderer.debugInfo()
  debug.render({
    clientTick: predictor?.state.tick ?? st?.tick ?? 0, serverTick: net.serverTick, snapTick: net.lastSnapshotTick, seq: predictor?.seq ?? 0,
    rtt: net.rtt, offset: net.clock.offset, predErr: predictor?.lastCorrection ?? 0, snapshotAgeMs: net.lastSnapshotAt ? now - net.lastSnapshotAt : 0, interpDepth: interp.size,
    state: me?.state ?? (spectator ? 'SPECTATOR' : '-'), stamina: me?.stamina ?? 0, shieldAge: me?.shieldAgeTicks ?? 0, hitstop: cf.frozen, fps,
    pendingInputs: predictor?.pending.size ?? 0, corrections: predictor?.corrections ?? 0, hardResyncs: predictor?.hardResyncs ?? 0, bytesIn: net.bytesIn,
    room: roomId, team: spectator ? `spec(cam ${cameraTeam === 0 ? 'A' : 'B'})` : cameraTeam === 0 ? 'A' : 'B', players: st?.players.length ?? 0, discs: st?.discs.length ?? 0,
    phase: st?.match.phase ?? '-', aim: `${((input.aim * 180) / Math.PI).toFixed(0)}deg${aimHit ? ` @(${aimHit.x.toFixed(1)},${aimHit.z.toFixed(1)})` : ''}`, device: input.usingGamepad ? 'gamepad' : 'mouse',
    fx: `sparks ${fx.sparks} decals ${fx.decals} undraw ${fx.timelines} ghosts ${fx.ghosts} bake ${fx.bakeMs.toFixed(0)}ms | audio ${audio.unlocked ? 'on' : 'locked'} voices ${audio.debug().voices.length} music ${audio.debug().music.state}`,
  })
}
requestAnimationFrame(frame)

;(window as unknown as { disclike: unknown }).disclike = {
  net, get predictor() { return predictor }, interp, input, options, audio, renderer, clock, hud, results, session, TICK_RATE, frameStats, resetFrameStats,
  get state() { return predictor?.state ?? serverState }, get roomId() { return roomId }, get playerId() { return playerId }, get spectator() { return spectator }, get cameraTeam() { return cameraTeam },
  /** Test hook (tools/e2e): projected virtual-px position of a player's chest, for CDP aiming. */
  screenOf(id: string) { const st = predictor?.state ?? serverState; const p = st?.players.find((q) => q.id === id); if (!p) return null; const pr = project(p.pos.x, 1.1, p.pos.z, cameraTeam, st!.arena, cam); return { x: pr.sx * vp.scale, y: pr.sy * vp.scale, visible: pr.visible } },
  get rig() { return rig }, get cam() { return cam }, get training() { return training },
  /** Fidelity V0: the active fixture (name, capture time) or null. */
  get fixture() { return fixture ? { name: fixtureName!, t: fixture.t } : null },
  /** Test hook (tools/e2e): the front screen. */
  menu: { get flow() { return flow }, enter(name?: string) { enterWorld(name) } },
  /** Test hook: current HUD text state for assertions. */
  hudState() { return { callout: hud.callouts.current(performance.now(), true)?.text ?? null, feed: hud.feed.rows(performance.now(), { hub: roomId.startsWith('hub') }).map((r) => r.data), results: results.visible ? results.lines() : null, resultsTitle: results.visible ? results.title() : null } },
}
